<template>
  <div class="p-2">
    <div class="row">
      <div class="col-lg-6">
        <div class="main-grid-wrapper">
          <div class="general-card card">
            <div class="card-body">
              <table class="table table-sm mb-0 general-info-table">
                <tr>
                  <td class="border-top-0"><b>{{ $t('Payment Terms') }}</b></td>
                  <td class="border-top-0">{{ $t("{count} days", {count: payment_terms}) }}</td>
                  <td class="border-top-0">
                    <b-button v-permission="'change_customer'" class="float-right p-0" variant="link"
                              @click="togglePaymentTermsModal({showModal: true, source: SOURCE_SALE_TERM_VIEW})">
                      <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                      {{ $t('Edit') }}
                    </b-button>
                  </td>
                </tr>
                <tr>
                  <td><b>{{ $t('Settlement Days') }}</b></td>
                  <td>{{ $t("{count} days", {count: settlement_days}) }}</td>
                  <td>
                    <b-button v-permission="'change_customer'" class="float-right p-0" variant="link"
                              @click="toggleSettlementDaysModal({showModal: true, source: SOURCE_SALE_TERM_VIEW})">
                      <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                      {{ $t('Edit') }}
                    </b-button>
                  </td>
                </tr>
                <tr>
                  <td><b>{{ $t('Exchange Rates') }}</b></td>
                  <td>
                    <ul class="mb-0 pl-3">
                      <li v-for="(amount, code) in currency_exchanges" :key="`current-row-${code}`">
                        {{ code }}: <b>{{ amount }}</b>
                      </li>
                    </ul>
                  </td>
                  <td>
                    <b-button v-permission="'change_customer'" class="float-right p-0" variant="link"
                              @click="toggleCurrencyExchangesModal({showModal: true, source: SOURCE_SALE_TERM_VIEW, currencyExchanges: currency_exchanges})">
                      <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                      {{ $t('Edit') }}
                    </b-button>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <div class="premiums-card card">
            <div class="card-header">
              <h5 class="d-inline-block mb-0">
                {{ $t('Premiums') }}
              </h5>
              <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                        @click="togglePremiumsModal({showModal: true, source: SOURCE_SALE_TERM_VIEW})">
                <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Edit') }}
              </b-button>
            </div>
            <div class="card-body pt-1 pb-0">
              <premiums-display-table :premiums="premiums" :minimum_premium="minimum_premium"
                                      :currency="sale.currency"/>
              <no-data-available v-if="!premiums || premiums.length === 0" :height="5"/>
            </div>
          </div>
          <div class="category-based-terms-card card">
            <div class="card-header">
              <h5 class="d-inline-block mb-0">
                {{ $t('Category Based Terms') }}
              </h5>
              <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                        @click="onAddAuctionCategoryTermClick()">
                <font-awesome-icon icon="plus" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Add') }}
              </b-button>
            </div>
            <spinner class="mt-3" show v-if="auctionCategoryTermsLoading"/>
            <div v-else class="card-body pb-0">
              <table class="info-table table table-sm" v-if="auctionCategoryTerms.length > 0">
                <tr>
                  <th class="border-top-0">{{ $t('Category') }}</th>
                  <th class="border-top-0">{{ $t('Commissions') }}</th>
                  <th class="border-top-0">{{ $t('Premiums') }}</th>
                  <th class="border-top-0" style="min-width: 70px">{{ $t('Tax Rate') }}</th>
                  <th class="border-top-0" style="width: 30px"></th>
                </tr>
                <tr v-for="t in auctionCategoryTerms" :key="`auction-category-term-row-${t.uuid}`">
                  <td>
                    {{ t.category.name | masterLanguage }}
                  </td>
                  <td>
                    <div v-for="(item, index) in getCalculatedTiers(t.commissions, 'commissions')"
                         :key="`commissions-row-auction-category-term-${index}`">
                      {{ item.percent | asPercentage }} - {{ item.amount_over | asCurrency }}
                    </div>
                  </td>
                  <td>
                    <div v-for="(item, index) in getCalculatedTiers(t.premiums, 'premiums')"
                         :key="`premiums-row-auction-category-term-${index}`">
                      {{ item.percent | asPercentage }} - {{ item.amount_over | asCurrency }}
                    </div>
                  </td>
                  <td>
                    <span v-if="t.tax_rate">
                      {{ t.tax_rate | asPercentage }}
                    </span>
                  </td>
                  <td>
                    <b-dropdown right class="text-white" variant="primary" no-caret size="sm">
                      <template #button-content>
                        <font-awesome-icon icon="ellipsis-v" class="icon"/>
                      </template>
                      <b-dd-item v-permission="'change_auction'" @click="onAuctionCategoryTermEditClick(t)">
                        <font-awesome-icon icon="pen" style="font-size: .9em" class="mr-1"/>
                        {{ $t('Edit') }}
                      </b-dd-item>
                      <b-dd-item v-permission="'change_auction'" @click="onAuctionCategoryTermDeleteClick(t)"
                                 variant="danger">
                        <font-awesome-icon icon="trash" style="font-size: .9em" class="mr-1"/>
                        {{ $t('Delete') }}
                      </b-dd-item>
                    </b-dropdown>
                  </td>
                </tr>
              </table>
              <no-data-available v-if="auctionCategoryTerms.length === 0" :height="5"/>
            </div>
          </div>
          <div class="col-md-12 col-xl-6 mt-3 d-flex">
            <div style="flex: 1" class="card">
              <div class="card-header">
                <h5 class="d-inline-block mb-0">
                  {{ $t('Increment Table') }}
                </h5>
                <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                          @click="toggleIncrementTableModal()">
                  <font-awesome-icon icon="pen" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Edit') }}
                </b-button>
              </div>
              <spinner class="mt-3" show v-if="incrementsLoading"/>
              <div v-else class="card-body pb-0">
                <table class="table table-sm mb-1 general-info-table">
                  <tr>
                    <td class="border-top-0"><b>{{ $t('Increment Calculation Source') }}</b></td>
                    <td class="border-top-0" style="text-transform: capitalize;">{{
                        sale.increment_calculation_source
                      }}
                    </td>
                    <td class="border-top-0">
                      <b-button v-permission="'change_customer'" class="float-right p-0" variant="link"
                                @click="toggleIncrementCalculationModal({showModal: true, source: SOURCE_SALE_TERM_VIEW})">
                        <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                        {{ $t('Edit') }}
                      </b-button>
                    </td>
                  </tr>
                </table>
                <hr>
                <table class="info-table table table-sm" v-if="increments.length > 0">
                  <tr>
                    <th>
                      {{ $t('Increment') }}
                    </th>
                    <th>
                      {{ $t('Upto') }}
                    </th>
                  </tr>
                  <tr v-for="(increment, index) in increments" :key="`increment-row-${index}`">
                    <td>
                      {{ increment.increment | asCurrency(sale.currency.symbol) }}
                    </td>
                    <td>
                      {{ (increments.length - 1) === index ? $t('And Up') : increment.max }}
                    </td>
                  </tr>
                </table>

                <no-data-available v-if="increments.length === 0" :height="5"/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6 mt-md-3 mt-lg-0">
        <div class="card">
          <div class="card-header">
            <h5 class="d-inline-block mb-0">
              {{ $t('Auction Settings') }}
            </h5>
            <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                      @click="onShowAuctionSettingsModal()">
              <font-awesome-icon icon="pen" style="font-size: .9em"></font-awesome-icon>
              {{ $t('Edit') }}
            </b-button>
          </div>
          <div class="card-body">
            <spinner show v-if="auctionSettingsLoading"/>
            <table v-else class="table table-sm">
              <tr>
                <td class="font-weight-bold border-top-0">{{ $t('Featured') }}</td>
                <td class="border-top-0">
                  <span class="badge bg-success text-white" v-if="sale.featured">{{ $t('Yes') }}</span>
                  <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('After Sale End Date') }}</td>
                <td>{{ sale.after_sale_end_date | toDateTime }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Clearance Start Date') }}</td>
                <td>{{ sale.clearance_start_date | toDateTime }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Clearance End Date') }}</td>
                <td>{{ sale.clearance_end_date | toDateTime }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Strict End Date') }}</td>
                <td>{{ sale.strict_end_date | toDateTime }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Clearance Comment') }}</td>
                <td>{{ sale.clearance_comment }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Viewing by Appointment') }}</td>
                <td>
                    <span v-if="sale.view_by_appointment === 'yes_with_dates'">
                        {{ $t('Yes with dates') }}
                    </span>
                  <span v-else-if="sale.view_by_appointment === 'yes'"
                        class="badge bg-success text-white">
                        {{ $t('Yes') }}
                    </span>
                  <span v-else-if="sale.view_by_appointment === 'no'"
                        class="badge bg-danger text-white">
                        {{ $t('No') }}
                    </span>
                  <span v-if="!sale.view_by_appointment"></span>
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Viewing Start Date') }}</td>
                <td>{{ sale.viewing_start_date | toDateTime }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Viewing End Date') }}</td>
                <td>{{ sale.viewing_end_date | toDateTime }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Private Viewing Start Date') }}</td>
                <td>{{ sale.private_viewing_start_date | toDateTime }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Private Viewing End Date') }}</td>
                <td>{{ sale.private_viewing_end_date | toDateTime }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Viewing Comment') }}</td>
                <td>{{ sale.viewing_comment }}</td>
              </tr>
              <tr v-if="isLive">
                <td class="font-weight-bold">{{ $t('Telephone Bid Request Cut off') }}</td>
                <td>{{ sale.telephone_bid_request_cut_off !== null && sale.telephone_bid_request_cut_off !== undefined ? $t('{minutes} Minutes', {minutes: sale.telephone_bid_request_cut_off}) : '-'}}</td>
              </tr>
              <tr v-if="isLive">
                <td class="font-weight-bold">{{ $t('Commission Bid Request Cut off') }}</td>
                <td>{{ sale.commission_bid_request_cut_off !== null && sale.commission_bid_request_cut_off !== undefined ? $t('{minutes} Minutes', {minutes: sale.commission_bid_request_cut_off}) : '-'}}</td>
              </tr>
              <tr v-if="isLive">
                <td class="font-weight-bold">{{ $t('Bidder Registration Cut off') }}</td>
                <td>{{ sale.bidder_registration_cut_off !== null && sale.bidder_registration_cut_off !== undefined ? $t('{minutes} Minutes', {minutes: sale.bidder_registration_cut_off}) : '-'}}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Stagger End Times') }}</td>
                <td>
                      <span v-if="sale.has_delay===false" class="badge bg-danger text-white">
                        {{ $t('No') }}
                      </span>
                  <span v-else-if="sale.has_delay===null">
                        {{ $t('No \'Stagger End Times\'') }}
                      </span>
                  <table v-else>
                    <tr>
                      <td class="border-top-0 pl-0">{{ $t('By:') }}</td>
                      <td class="border-top-0">{{ lotDelaySecondsValue }}</td>
                    </tr>
                    <tr>
                      <td class="pl-0">{{ $t('Every:') }}</td>
                      <td>{{ sale.lot_delay_batch_size }}</td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Buy Now Cut Off') }}</td>
                <td>{{ buyNowCutOffDaysValue }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Bidding Extension') }}</td>
                <td>{{ biddingExtensionValue }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Max Total Delay') }}</td>
                <td>{{ maxTotalDelayValue }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('Sales Taxes') }}</td>
                <td>
                      <span v-if="sale.tax_required===false" class="badge bg-danger text-white">
                        {{ $t('No') }}
                      </span>
                  <span v-else-if="sale.tax_required===null">
                        {{ $t('No \'Sales Taxes\'') }}
                      </span>
                  <table v-else>
                    <tr>
                      <td class="border-top-0 pl-0">{{ $t('Hammer Tax Rate:') }}</td>
                      <td class="border-top-0">{{ !sale.hammer_tax_rate ? 0 : sale.hammer_tax_rate }}</td>
                    </tr>
                    <tr>
                      <td class="pl-0">{{ $t('BP Tax Rate:') }}</td>
                      <td>{{ !sale.bp_tax_rate ? 0 : sale.bp_tax_rate }}</td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>
    <premiums-modal/>
    <payment-terms-modal/>
    <settlement-days-modal/>
    <increments-modal/>
    <auction-settings-modal/>
    <increment-calculation-modal/>
  </div>
</template>

<script>

  import {createNamespacedHelpers} from 'vuex';
  import NoDataAvailable from "../../../../core/components/common/NoDataAvailable";
  import PremiumsModal from "./modals/PremiumsModal";
  import PaymentTermsModal from "./modals/PaymentTermsModal";
  import SettlementDaysModal from "./modals/SettlementDaysModal";
  import {SOURCE_SALE_TERM_VIEW, SOURCE_TIMED_AUCTION_SETTINGS} from "../../../constants";
  import IncrementsModal from "./modals/IncrementsModal";
  import Spinner from "@/core/components/common/Spinner";
  import AuctionSettingsModal from "@/modules/sales/view/terms/modals/AuctionSettingsModal";
  import helperService from "@/core/services/helper.service";
  import cloneDeep from "lodash/cloneDeep";
  import IncrementCalculationModal from "@/modules/sales/view/terms/modals/IncrementCalculationModal.vue";
  import PremiumsDisplayTable from "@/modules/sales/view/terms/PremiumsDisplayTable";

  const {mapState, mapActions} = createNamespacedHelpers('sales');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "Terms",
    components: {
      IncrementCalculationModal,
      PremiumsDisplayTable,
      AuctionSettingsModal,
      Spinner,
      IncrementsModal,
      PaymentTermsModal,
      SettlementDaysModal,
      PremiumsModal,
      NoDataAvailable,
    },
    data() {
      return {
        SOURCE_SALE_TERM_VIEW: SOURCE_SALE_TERM_VIEW,
      }
    },
    methods: {
      ...mapActions([
        'togglePremiumsModal',
        'togglePaymentTermsModal',
        'toggleSettlementDaysModal',
        'getTermsData',
        'showIncrementModal',
        'showIncrementCalculationModal',
        'getIncrements',
        'showAuctionSettingsModal',
        'toggleCurrencyExchangesModal',
        'showAuctionCategoryTermModal',
        'getAuctionCategoryTerms',
        'deleteAuctionCategoryTerm',
      ]),
      toggleIncrementTableModal() {
        this.showIncrementModal({
          objects: this.increments,
          source: 'increments-table',
          auction_uuid: this.sale.uuid
        });
      },
      toggleIncrementCalculationModal() {
        this.showIncrementCalculationModal({
          object: this.sale,
          source: SOURCE_SALE_TERM_VIEW
        });
      },
      onShowAuctionSettingsModal() {
        this.showAuctionSettingsModal({
          object: this.sale,
          source: SOURCE_TIMED_AUCTION_SETTINGS,
          auction_uuid: this.sale.uuid
        });
      },
      onAddAuctionCategoryTermClick() {
        this.showAuctionCategoryTermModal({
          source: SOURCE_SALE_TERM_VIEW,
          auction: this.sale
        })
      },
      getCalculatedTiers(tiers, key) {
        let data = helperService.reverseCalculateTiers({[key]: cloneDeep(tiers)})
        return data[key]
      },
      onAuctionCategoryTermEditClick(item) {
        this.showAuctionCategoryTermModal({
          source: SOURCE_SALE_TERM_VIEW,
          auction: this.sale,
          object: item
        })
      },
      onAuctionCategoryTermDeleteClick(item) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete following auction category term? If there are any lots attached, respective terms will be reverted to defaults."),
          this.$i18n.t('Auction category term was successfully deleted'),
          this.deleteAuctionCategoryTerm,
          item,
        );
      },
    },
    computed: {
      ...mapState({
        sale: state => state.view.currentSale,
        premiums: state => {
          let data = helperService.reverseCalculateTiers({premiums: cloneDeep(state.view.termsData.premiums)})
          return data.premiums
        },
        minimum_premium: state => state.view.termsData.minimum_premium,
        payment_terms: state => state.view.termsData.payment_terms,
        settlement_days: state => state.view.termsData.settlement_days,
        currency_exchanges: state => state.view.termsData.currency_exchanges,
        increments: state => state.view.incrementData.increments,
        incrementsLoading: state => state.view.incrementData.loading,
        auctionSettingsLoading: state => state.view.auctionSettingsData.loading,
        auctionCategoryTermsLoading: state => state.view.auctionCategoryTermsData.loading,
        auctionCategoryTerms: state => state.view.auctionCategoryTermsData.auctionCategoryTerms,
      }),
      ...mapStateGlobal({
        lotDelaySecondsOptions: state => state.lotDelaySecondsOptions,
        buyNowCutOffDaysOptions: state => state.buyNowCutOffDaysOptions,
        biddingExtensionOptions: state => state.biddingExtensionOptions,
        maxTotalDelayOptions: state => state.maxTotalDelayOptions,
      }),
      lotDelaySecondsValue() {
        let value = this.lotDelaySecondsOptions.find(i => i.value === this.sale.lot_delay_seconds)
        if (value) {
          return value.key;
        } else {
          return ''
        }
      },
      buyNowCutOffDaysValue() {
        let value = this.buyNowCutOffDaysOptions.find(i => i.value === this.sale.buy_now_cut_off_days)
        if (value) {
          return value.key;
        } else {
          return this.$t("No 'Buy Now' option");
        }
      },
      biddingExtensionValue() {
        let value = this.biddingExtensionOptions.find(i => i.value === this.sale.bidding_extension)
        if (value) {
          return value.key;
        } else {
          return this.$t("No Extension");
        }
      },
      maxTotalDelayValue() {
        let value = this.maxTotalDelayOptions.find(i => i.value === this.sale.max_total_delay)
        if (value) {
          return value.key;
        } else {
          return this.$t("No 'Max Total Delay' option");
        }
      },
      isLive() {
        return this.sale && this.sale.type === 'live'
      },
    },
    mounted() {
      this.getIncrements(this.sale.uuid);
      this.getAuctionCategoryTerms(this.sale.uuid);
      this.getTermsData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "./../../scss/variables";

  .general-info-table {

    button {
      visibility: hidden;
    }

    td:first-child {
      width: 15em;
    }


    tr:hover {
      button {
        visibility: visible;
      }

      td {
        background-color: lighten($secondary, 6);
      }
    }
  }

  .main-grid-wrapper {
    display: grid;
    grid-gap: 1em;
    grid-template-columns: 2fr 2fr;

    .general-card {
      grid-column: 1/2;
    }

    .premiums-card {
      grid-column: 2/3;
    }

    .category-based-terms-card {
      grid-column: 1/3;
    }

    .increments-card {
      grid-column: 1/2;
    }
  }

  @media only screen and (max-width: map-get($grid-breakpoints, "xl")) {
    .main-grid-wrapper {
      .general-card {
        grid-column: 1/3;
      }

      .premiums-card {
        grid-column: 1/3;
      }

      .category-based-terms-card {
        grid-column: 1/3;
      }

      .increments-card {
        grid-column: 1/3;
      }
    }

  }

</style>

